import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Circle, TrendingUp } from "lucide-react";
import api from "@/lib/api";

const STEPS = [
  { k: "change", title: "Log a real change", why: "The example shows the shape; the trial proves itself on your own work.", to: "/app/start", cta: "Start a change" },
  { k: "stakeholders", title: "Map who it lands on", why: "Agreement and trust per person — everything downstream hangs off this.", to: "/app/changes", cta: "Map stakeholders" },
  { k: "baseline", title: "Baseline readiness (by day 7)", why: "First of two points. Without it there is no trend to show.", to: "/app/changes", cta: "Run baseline", due: 7 },
  { k: "followup", title: "Follow-up assessment (around day 20)", why: "Second point. This is the demonstration the trial exists for.", to: "/app/changes", cta: "Re-assess", due: 20 },
];

// Dashboard card for trialing orgs: the three-plus-one milestones that make the readiness trend visible before the trial ends.
export default function TrialProgressCard() {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/billing/summary").then((r) => setT(r.data.state === "trialing" && r.data.trial && !r.data.billed_by ? r.data.trial : null)).catch(() => setT(null)); }, []);
  if (!t?.milestones) return null;
  const m = t.milestones;
  const done = STEPS.filter((s) => m[s.k]).length;
  const next = STEPS.find((s) => !m[s.k]);
  return (
    <section className="pp-card p-5 mb-6 border-accent-200" data-testid="trial-progress-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <div className="text-xs font-bold uppercase tracking-widest text-accent-700 inline-flex items-center gap-1"><TrendingUp size={13} /> Trial · day {t.day} of {t.trial_days} · {t.days_left} days left</div>
          <div className="font-head text-lg font-extrabold mt-1">{done === STEPS.length ? "Trend demonstrated — you've seen ChangeOS do its job" : `${done} of ${STEPS.length} milestones — ${next?.title.toLowerCase()} next`}</div>
          <p className="text-sm text-subtle mt-0.5">The trend needs two readiness assessments separated by real time. That's why the trial is 30 days and why these four steps matter.</p>
        </div>
        {next && <Link to={next.to} data-testid="trial-progress-cta" className="pp-btn-dark shrink-0">{next.cta}</Link>}
      </div>
      <ol className="grid sm:grid-cols-4 gap-3 mt-4">
        {STEPS.map((s, i) => {
          const ok = m[s.k];
          const overdue = !ok && s.due && t.day >= s.due && STEPS.slice(0, i).every((p) => m[p.k]);
          return (
            <li key={s.k} data-testid={`trial-milestone-${s.k}`} className={`rounded-xl border p-3 text-sm ${ok ? "border-success-200 bg-success-50/60" : overdue ? "border-warning-300 bg-warning-50" : "border-border"}`}>
              <div className="flex items-center gap-2 font-bold text-foreground">{ok ? <Check size={14} className="text-success-600" /> : <Circle size={14} className={overdue ? "text-warning-600" : "text-faint"} />} {s.title}</div>
              <div className="text-xs text-subtle mt-1">{s.why}</div>
              {overdue && <div className="text-xs font-bold text-warning-800 mt-1" data-testid={`trial-milestone-due-${s.k}`}>Due now — day {t.day}</div>}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
